<template>
  <div class="flex flex-col h-full">
    <BaseDetailsPreviewEditHeader
      v-if="visitLogin"
      :title="`${visitLogin?.visit?.traderSite?.name || ''} | ${visitLogin?.visit?.salesPerson?.name || ''} | ${isoDateToFullDateTime(visitLogin?.loginDateStamp) || ''}`"
      link-name="questionnaires-report"
      :mode="mode"
      :hide-layout-options="true" />
    <div class="py-4">
      <BaseTable
        v-if="questionnaireTransactions"
        :headers="tableHeaders"
        :list="questionnaireTransactions"
        :total-records="questionnaireTransactions.length"
        :is-add-new-hidden="true"
        :group-by-headers="groupHeaders"
        clickable
        table-id="reportsAnswersDetails"
        :hide-multiselect-delete="true">
        <template #custom="{ item, header }">
          <td v-if="header === 'answerValue'">
            <template
              v-if="item.qstTransactionMaster?.qstQuestion?.mwDataType?.name === MWDataTypes.IMAGE">
              <img
                v-if="item.answerValue && item.answerValue.length > 100"
                :src="'data:image/jpg;base64,' + item.answerValue"
                alt="Image"
                class="max-w-sm max-h-16" />
              <span v-else>No image data</span>
            </template>
            <template v-else>
              {{ item.answerValue }}
            </template>
          </td>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import type {
  QSTTransactionDetails,
  QstTransactionDetailsTableFilterData,
} from '@/types/QSTTransactionDetails';
import { useI18n } from 'vue-i18n';
import type { VisitLogin } from '@/types/VisitLogin';
import {
  getReportsAnswersDetailsWithConditions,
  getVisitLoginById,
} from '@/services/api/questionnaires';
import { isoDateToFullDateTime } from '@/utils/date';
import { eBindingType } from '@/types/Questionnaires';
import { MWDataTypes } from '@/utils/constants';

const { t: $t } = useI18n({ useScope: 'global' });
const route = useRoute();
const mode = ref<'view' | 'edit'>('view');
const questionnaireTransactions = ref<QSTTransactionDetails[]>([]);
const visitLoginId = ref<string>();
const visitLogin = ref<VisitLogin>();

const tableHeaders = [
  {
    value: 'qstTransactionMaster.qstQuestion.qstQuestionnaire.title',
    text: $t('questionnaire.questionnaireTitle'),
    type: 'text',
    active: true,
  },
  {
    value: 'qstTransactionMaster.qstQuestion.title',
    text: $t('questionnaire.questionTitle'),
    type: 'text',
    active: true,
  },
  {
    value: 'bindingTypeString',
    text: $t('questionnaire.bindingType'),
    type: 'text',
    active: true,
  },
  { value: 'material.name', text: $t('material.material'), type: 'text', active: true },
  { value: 'groupItem.name', text: $t('questionnaire.groupItem'), type: 'text', active: true },
  {
    value: 'qstTransactionMaster.qstQuestion.mwDataType.name',
    text: $t('question.dataType'),
    type: 'text',
    active: true,
  },
  { value: 'answerValue', text: $t('question.answerValue'), type: 'custom', active: true },
  {
    value: 'qstActionDetail.description',
    text: $t('baseObject.actions'),
    type: 'text',
    active: true,
  },
];

const groupHeaders = [
  {
    value: 'qstTransactionMaster.qstQuestion.qstQuestionnaire.title',
    text: $t('questionnaire.questionnaireTitle'),
    type: 'text',
    active: true,
  },
  {
    value: 'qstTransactionMaster.qstQuestion.title',
    text: $t('questionnaire.questionTitle'),
    type: 'text',
    active: true,
  },
];

onMounted(async () => {
  visitLoginId.value = route.params.id as string;
  visitLogin.value = await getVisitLoginById(visitLoginId.value);
  loadData();
});

async function loadData(params?: QstTransactionDetailsTableFilterData) {
  if (!visitLoginId.value) return;
  if (!params) params = { visitLoginId: visitLoginId.value };
  const response = await getReportsAnswersDetailsWithConditions(params);
  if (response) {
    questionnaireTransactions.value = response.data.map((item) => ({
      ...item,
      bindingTypeString: getQstBindingTypeString(
        item.qstTransactionMaster?.qstQuestion?.qstQuestionnaire?.bindingType
      ),
    }));
  }
}

function getQstBindingTypeString(eType: number | undefined): string {
  switch (eType) {
    case eBindingType.Generic:
      return 'Generic';
    case eBindingType.Material_groups:
      return 'Material groups';
    case eBindingType.Materials:
      return 'Materials';
    default:
      return 'Unknown';
  }
}
</script>
